import { useState, useRef, useEffect } from 'react';
import { useAuth } from 'auth_context';

export default function OrgAutocomplete({ onSelect, placeholder = 'Search for your organization...' }) {
    const auth = useAuth();
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = async (value) => {
        if (!value || value.length < 2) {
            setSuggestions([]);
            return;
        }
        try {
            const res = await auth.authFetch(`/api/search_orgs/?q=${encodeURIComponent(value)}`);
            const data = await res.json();
            setSuggestions(data.orgs || []);
            setOpen(true);
        } catch {
            setSuggestions([]);
        }
    };

    const handleChange = (e) => {
        const value = e.target.value;
        setQuery(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchSuggestions(value), 300);
    };

    const handleSelect = (org) => {
        setQuery(org.name);
        setOpen(false);
        setSuggestions([]);
        onSelect(org);
    };

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
            <input
                ref={inputRef}
                className="org-detail-input"
                value={query}
                onChange={handleChange}
                placeholder={placeholder}
                autoComplete="off"
            />
            {open && suggestions.length > 0 && (() => {
                const rect = inputRef.current?.getBoundingClientRect();
                const wrapperRect = wrapperRef.current?.getBoundingClientRect();
                const offsetLeft = rect ? rect.left - wrapperRect.left : 0;
                const offsetWidth = rect ? rect.width : '100%';
                return (
                <ul style={{
                    position: 'absolute',
                    top: '100%',
                    left: offsetLeft,
                    width: offsetWidth,
                    background: '#2e2e2e',
                    color: 'white',
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    borderRadius: '0 0 6px 6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    zIndex: 1000,
                    maxHeight: '200px',
                    overflowY: 'auto',
                }}>
                    {suggestions.map(org => (
                        <li
                            key={org.id}
                            onMouseDown={() => handleSelect(org)}
                            style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                fontSize: '0.9rem',
                                textAlign: 'left',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#444'}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                            <div>{org.name}</div>
                            {(org.city || org.state) && (
                                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
                                    {[org.city, org.state].filter(Boolean).join(', ')}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
                );
            })()}
        </div>
    );
}
